'use client'

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState, useEffect } from 'react'
import useDebounce from '@/useDebounce'

export const SearchBoxClient = ({ initialQuery }: SearchBoxClientProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [searchState, setSearchState] = useState<SearchState>({
    query: initialQuery,
    draft: null,
  })

  // URL changes are authoritative; discard any draft created for the previous query.
  if (searchState.query !== initialQuery) {
    setSearchState({
      query: initialQuery,
      draft: null,
    })
  }

  const searchTerm =
    searchState.query === initialQuery
      ? (searchState.draft ?? initialQuery)
      : initialQuery
  const debouncedSearchState = useDebounce(searchState, 500)

  useEffect(() => {
    if (
      debouncedSearchState.query === initialQuery &&
      debouncedSearchState.draft !== null &&
      debouncedSearchState.draft !== initialQuery
    ) {
      const params = new URLSearchParams(searchParams.toString())
      if (debouncedSearchState.draft) {
        params.set('query', debouncedSearchState.draft)
      } else {
        params.delete('query')
      }
      params.set('page', '1') // Reset to page 1
      router.push(`/search?${params.toString()}`)
    }
  }, [debouncedSearchState, initialQuery, searchParams, router])

  return (
    <div className="w-full min-w-0 md:flex-1">
      <label htmlFor="searchTerm" className="sr-only">
        Search
      </label>
      <p className="hidden md:block text-sm text-gray-500 mb-1">Search</p>
      <div className="relative rounded-md shadow-xs">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="size-5 text-gray-400"
          />
        </div>
        <input
          value={searchTerm}
          onChange={e =>
            setSearchState({
              query: initialQuery,
              draft: e.target.value,
            })
          }
          id="searchTerm"
          type="text"
          placeholder="Search..."
          className="block w-full rounded-md border-0 py-2.5 pl-10 pr-10 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-vi-gray sm:text-lg/6"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() =>
              setSearchState({
                query: initialQuery,
                draft: '',
              })
            }
            className="absolute inset-y-0 right-0 flex items-center pr-3"
          >
            <XMarkIcon
              className="size-4 text-gray-400 hover:text-gray-500"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    </div>
  )
}

interface SearchBoxClientProps {
  initialQuery: string
}

interface SearchState {
  query: string
  draft: string | null
}
